import axios from 'axios';
import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, X, ZoomIn, Briefcase, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EditeurPhotoZone } from "./EditeurPhotoZone";
import { useAuth } from "@/lib/AuthContext";
import { urlFichier } from '@/lib/fichiers';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function PhotosTravail({ dossier, bonsInterieur, bonsExterieur, onUpdate }) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedBTInterieur, setSelectedBTInterieur] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [showEditeur, setShowEditeur] = useState(false);
  const [showBTExterieurDialog, setShowBTExterieurDialog] = useState(false);
  const [selectedPhotoForZone, setSelectedPhotoForZone] = useState(null);
  const [selectedBTExterieur, setSelectedBTExterieur] = useState('');
  const fileInputRef = useRef(null);

  const photosTravaul = dossier?.photos_travail || [];
  const userRole = user?.role || '';
  // Permissions: représentants, CP, admin peuvent uploader
  const canUpload = ['representante', 'charge_projet', 'adjoint_admin', 'proprietaire'].includes(userRole);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    const { compressImageIfNeeded } = await import('@/lib/imageCompression');
    for (const file of files) {
      try {
        const compressed = await compressImageIfNeeded(file);
        const formData = new FormData();
        formData.append("file", compressed);
        const uploadRes = await axios.post(`${API_URL}/api/crm/upload-photo`, formData);
        const uploadData = uploadRes.data;
        if (!uploadData.photo_url) throw new Error("Upload échoué");

        const addRes = await axios.post(`${API_URL}/api/crm/dossiers/${dossier.id}/photos-travail`, { 
          photo_url: uploadData.photo_url 
        });
        if (addRes.status !== 200) throw new Error("Ajout échoué");
        uploaded++;
      } catch (err) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }
    setUploading(false);
    if (uploaded > 0) {
      toast.success(`${uploaded} photo(s) de travail ajoutée(s)`);
      onUpdate();
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = async (photoUrl) => {
    try {
      await axios.delete(`${API_URL}/api/crm/dossiers/${dossier.id}/photos-travail`, {
        data: { photo_url: photoUrl }
      });
      toast.success("Photo supprimée");
      onUpdate();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const handleTransferAll = async () => {
    if (!selectedBTInterieur) {
      toast.error("Veuillez sélectionner un BT Intérieur");
      return;
    }
    setTransferring(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/crm/dossiers/${dossier.id}/transferer-photos-bt-interieur`,
        { bon_id: selectedBTInterieur }
      );
      if (res.status === 200) {
        const btNom = bonsInterieur.find(b => b.id === selectedBTInterieur)?.nom || 'BT Intérieur';
        toast.success(`${res.data.count || 0} photo(s) transférée(s) vers ${btNom}`);
        setShowTransferDialog(false);
        setSelectedBTInterieur('');
        onUpdate();
      }
    } catch (err) {
      toast.error("Erreur lors du transfert");
    } finally {
      setTransferring(false);
    }
  };

  const handleOpenEditeur = (photoUrl) => {
    if (bonsExterieur.length === 0) {
      toast.error("Aucun BT Extérieur disponible");
      return;
    }
    
    setSelectedPhotoForZone(photoUrl);
    
    if (bonsExterieur.length === 1) {
      // Un seul BT → ouvrir directement l'éditeur
      setSelectedBTExterieur(bonsExterieur[0].id);
      setShowEditeur(true);
    } else {
      // Plusieurs BT → afficher dialogue de sélection
      setShowBTExterieurDialog(true);
    }
  };

  const handleConfirmBTExterieur = () => {
    if (!selectedBTExterieur) {
      toast.error("Veuillez sélectionner un BT Extérieur");
      return;
    }
    setShowBTExterieurDialog(false);
    setShowEditeur(true);
  };

  const hasBTInterieur = bonsInterieur && bonsInterieur.length > 0;
  const hasBTExterieur = bonsExterieur && bonsExterieur.length > 0;

  return (
    <div className="space-y-4 mt-6">
      <Card className="border-orange-200">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-orange-600" />
              Photos de Travail
              <span className="text-sm font-normal text-slate-500">
                ({photosTravaul.length} photo{photosTravaul.length > 1 ? "s" : ""})
              </span>
            </CardTitle>
            {canUpload && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {uploading ? "Envoi..." : "Ajouter photos"}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {!canUpload && (
            <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              ℹ️ Vous n'avez pas la permission d'uploader des photos de travail.
            </div>
          )}

          {photosTravaul.length === 0 ? (
            <div
              className="border-2 border-dashed border-orange-200 rounded-xl p-12 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
              onClick={() => canUpload && fileInputRef.current?.click()}
            >
              <ImageIcon className="w-12 h-12 text-orange-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Aucune photo de travail</p>
              <p className="text-sm text-slate-500 mt-1">
                Uploadez des photos pour les utiliser sur les bons de travail
              </p>
            </div>
          ) : (
            <>
              {/* Bouton global : Transfert en masse vers BT Intérieur */}
              {hasBTInterieur && (
                <div className="mb-4 flex justify-end">
                  <Button
                    onClick={() => setShowTransferDialog(true)}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    📦 Transférer toutes vers BT Intérieur
                  </Button>
                </div>
              )}

              {/* Galerie de photos */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {photosTravaul.map((url, idx) => (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-orange-200 bg-white">
                    {/* Image */}
                    <div className="aspect-square">
                      <img
                        src={urlFichier(url)}
                        alt={`Photo travail ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=Photo"; }}
                      />
                    </div>

                    {/* Overlay Hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-7 w-full text-xs"
                        onClick={() => setPreviewUrl(url.startsWith("/api") ? `${API_URL}${url}` : url)}
                      >
                        <ZoomIn className="w-3 h-3 mr-1" /> Voir
                      </Button>

                      {hasBTExterieur && (
                        <Button
                          size="sm"
                          className="h-7 w-full text-xs bg-green-600 hover:bg-green-700"
                          onClick={() => handleOpenEditeur(url)}
                        >
                          🖼️ Créer zone BT Ext
                        </Button>
                      )}

                      {canUpload && (
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 w-full text-xs"
                          onClick={() => handleRemove(url)}
                        >
                          <X className="w-3 h-3 mr-1" /> Supprimer
                        </Button>
                      )}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 bg-orange-600/90 text-white text-xs text-center py-0.5">
                      Photo {idx + 1}
                    </div>
                  </div>
                ))}

                {/* Bouton Ajouter */}
                {canUpload && (
                  <div
                    className="border-2 border-dashed border-orange-200 rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-6 h-6 text-orange-400 mb-1" />
                    <span className="text-xs text-orange-600">Ajouter</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogue de transfert en masse vers BT Intérieur */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transférer toutes les photos vers un BT Intérieur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Toutes les photos de travail ({photosTravaul.length}) seront transférées vers le BT sélectionné.
            </p>
            <div>
              <label className="text-sm font-medium mb-2 block">Sélectionner le BT Intérieur</label>
              <Select value={selectedBTInterieur} onValueChange={setSelectedBTInterieur}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un BT" />
                </SelectTrigger>
                <SelectContent>
                  {bonsInterieur.map((bt) => (
                    <SelectItem key={bt.id} value={bt.id}>
                      {bt.nom || `BT Intérieur ${bt.id.slice(0, 6)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleTransferAll}
                disabled={!selectedBTInterieur || transferring}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {transferring ? "Transfert..." : "Transférer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue de sélection BT Extérieur pour création de zone */}
      <Dialog open={showBTExterieurDialog} onOpenChange={setShowBTExterieurDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sélectionner un BT Extérieur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Sur quel BT Extérieur souhaitez-vous créer une zone avec cette photo ?
            </p>
            <div>
              <label className="text-sm font-medium mb-2 block">Sélectionner le BT Extérieur</label>
              <Select value={selectedBTExterieur} onValueChange={setSelectedBTExterieur}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un BT" />
                </SelectTrigger>
                <SelectContent>
                  {bonsExterieur.map((bt) => (
                    <SelectItem key={bt.id} value={bt.id}>
                      {bt.nom || `BT Extérieur ${bt.id.slice(0, 6)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBTExterieurDialog(false);
                  setSelectedBTExterieur('');
                  setSelectedPhotoForZone(null);
                }}
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirmBTExterieur}
                disabled={!selectedBTExterieur}
                className="bg-green-600 hover:bg-green-700"
              >
                Continuer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen preview dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-4xl p-2">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Aperçu"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Éditeur de zone (react-konva) */}
      {showEditeur && selectedPhotoForZone && selectedBTExterieur && (
        <EditeurPhotoZone
          photoUrl={selectedPhotoForZone.startsWith("/api") ? `${API_URL}${selectedPhotoForZone}` : selectedPhotoForZone}
          photoUrlOriginal={selectedPhotoForZone}
          bonId={selectedBTExterieur}
          dossierId={dossier.id}
          onClose={() => {
            setShowEditeur(false);
            setSelectedPhotoForZone(null);
            setSelectedBTExterieur('');
          }}
          onZoneCreated={() => {
            setShowEditeur(false);
            setSelectedPhotoForZone(null);
            setSelectedBTExterieur('');
            onUpdate();
          }}
        />
      )}
    </div>
  );
}

export default PhotosTravail;
